import { trackError } from '@billy/track-error'
import { DatadogProvider } from '@datadog/openfeature-browser'
import { EvaluationContext, OpenFeature } from '@openfeature/react-sdk'

import { REMOTE_FEATURE_FLAG_PROXY_PATH } from './constants'

let initPromise: Promise<void> | null = null
let isProviderRegistered = false
let isConfigUnavailable = false
let lastTargetingKey: string | null = null
let lastProviderError: string | null = null

const getFlaggingProxyUrl = (): string => `${window.location.origin}${REMOTE_FEATURE_FLAG_PROXY_PATH}`

const registerProvider = async (context?: EvaluationContext): Promise<void> => {
  const datadogConfig = window.ENV?.datadogConfig
  const { applicationId, clientToken, site, env, service, version } = datadogConfig ?? {}

  if (!applicationId || !clientToken || !site || !env) {
    isConfigUnavailable = true
    lastProviderError = 'Missing datadogConfig fields required for OpenFeature'
    return
  }

  const provider = new DatadogProvider({
    applicationId,
    clientToken,
    // Leave these off for the POC
    enableExposureLogging: false,
    enableRumFeatureFlagTracking: false,
    env,
    flaggingProxy: getFlaggingProxyUrl(),
    service,
    site,
    version,
  })

  if (context) {
    await OpenFeature.setProviderAndWait(provider, context)
  } else {
    await OpenFeature.setProviderAndWait(provider)
  }

  isProviderRegistered = true
  lastTargetingKey = context?.targetingKey ?? null
  lastProviderError = null
}

export const initRemoteFeatureFlags = async (context?: EvaluationContext): Promise<void> => {
  if (isConfigUnavailable) {
    return
  }

  try {
    if (!initPromise) {
      initPromise = registerProvider(context).catch((error) => {
        initPromise = null
        throw error
      })
    }

    await initPromise

    if (!isProviderRegistered) {
      return
    }

    if (!context?.targetingKey || context.targetingKey === lastTargetingKey) {
      return
    }

    await OpenFeature.setContext(context)
    lastTargetingKey = context.targetingKey
    lastProviderError = null
  } catch (error) {
    lastProviderError = error instanceof Error ? error.message : String(error)
    trackError(error)
  }
}

export const isRemoteFeatureFlagsReady = (): boolean => isProviderRegistered

export const getRemoteFeatureFlagProviderError = (): string | null => lastProviderError
